import React from 'react';
import { Link } from 'react-router-dom';
import { GUIDE_GROUPS } from '../lib/guideArchitecture';
import { novidadesBannerEnabled } from '../lib/flags';
import SafeIcon from '../common/SafeIcon';
import ContactEmail from './ContactEmail';
import { useLocale } from './LocaleProvider';

// Rodapé = faixa "floresta": o verde-pinho profundo fecha todas as páginas (o único bloco escuro do sistema).
const linkClass = 'inline-flex min-h-11 items-center text-[var(--on-forest-2)] no-underline hover:text-[var(--on-forest)] hover:underline md:min-h-7';
const eyebrowClass = 'eyebrow mb-3 text-[var(--on-forest-2)]';

export default function Footer() {
  const { locale } = useLocale();
  const isSpanish = locale === 'es';
  return (
    <footer className={`band-forest mt-24 pt-14 text-sm no-print ${novidadesBannerEnabled() ? 'pb-20' : 'pb-10'}`}>
      <div className="max-w-[1180px] mx-auto px-4">
        <span aria-hidden="true" className="eyebrow-rule mb-6" />
        <div className="grid gap-8 md:grid-cols-4 mb-10">
          <div>
            <p className="flex items-center gap-2 text-base font-semibold mb-3 text-[var(--on-forest)]">
              <span className="flex items-end gap-[2px]" aria-hidden="true">
                <span className="h-3 w-[3px] bg-[var(--on-forest-2)]" />
                <span className="h-4 w-[3px] bg-[var(--on-forest)]" />
                <span className="h-2.5 w-[3px] bg-[var(--on-forest-2)]" />
              </span>
              MiniMental{isSpanish ? '.org' : '.com.br'}
            </p>
            <p className="leading-relaxed text-[var(--on-forest-2)]">
              {isSpanish ? 'Recurso educativo sobre el Mini Examen del Estado Mental para profesionales de salud.' : 'Recurso educacional brasileiro sobre o Mini Exame do Estado Mental, para profissionais de saúde.'}
            </p>
          </div>

          <nav aria-label={isSpanish ? 'Navegación' : 'Navegação'}>
            <p className={eyebrowClass}>{isSpanish ? 'Navegación' : 'Navegação'}</p>
            <ul className="space-y-0.5">
              <li><Link to={isSpanish ? '/es' : '/#calculadora'} className={linkClass}>{isSpanish ? 'Calculadora' : 'Calculadora'}</Link></li>
              <li><Link to={isSpanish ? '/es' : '/guias'} className={linkClass}>{isSpanish ? 'Guías del MMSE' : 'Guias do MEEM'}</Link></li>
              <li><Link to="/pro" className={linkClass}>MiniMental PRO</Link></li>
              <li><Link to={isSpanish ? '/es/ayuda' : '/suporte'} data-analytics-event="support_clicked" className={linkClass}>{isSpanish ? 'Ayuda' : 'Suporte'}</Link></li>
              <li><Link to={isSpanish ? '/es/mini-examen-del-estado-mental' : '/#o-que-e'} className={linkClass}>{isSpanish ? 'Qué es el MMSE' : 'O que é o MEEM'}</Link></li>
              <li><Link to={isSpanish ? '/es/mini-examen-del-estado-mental' : '/#referencias'} className={linkClass}>{isSpanish ? 'Referencias científicas' : 'Referências científicas'}</Link></li>
            </ul>
          </nav>

          <nav aria-label={isSpanish ? 'Guías por tarea' : 'Guias por tarefa'}>
            <p className={eyebrowClass}>{isSpanish ? 'Guías por tarea' : 'Guias por tarefa'}</p>
            <ul className="space-y-0.5">
              {isSpanish ? [
                { id: 'test-mini-mental-online', title: 'Test Mini Mental online', to: '/es/test-mini-mental-online' },
                { id: 'mini-examen-del-estado-mental', title: 'Mini Examen del Estado Mental', to: '/es/mini-examen-del-estado-mental' },
                { id: 'calculadora-mini-mental', title: 'Calculadora Mini Mental', to: '/es/calculadora-mini-mental' },
              ].map((group) => (
                <li key={group.id}><Link to={group.to} className={linkClass}>{group.title}</Link></li>
              )) : GUIDE_GROUPS.map((group) => (
                <li key={group.id}>
                  <Link to={`/guias#${group.id}`} className={linkClass}>{group.title}</Link>
                </li>
              ))}
            </ul>
          </nav>

          <nav aria-label={isSpanish ? 'Acerca de y legal' : 'Sobre e legal'}>
            <p className={eyebrowClass}>{isSpanish ? 'Acerca de y legal' : 'Sobre e legal'}</p>
            <ul className="space-y-0.5">
              <li><Link to={isSpanish ? '/es/acerca-de' : '/sobre'} className={linkClass}>{isSpanish ? 'Acerca de' : 'Sobre'}</Link></li>
              <li><Link to={isSpanish ? '/es/politica-de-privacidad' : '/politica-de-privacidade'} className={linkClass}>{isSpanish ? 'Política de privacidad' : 'Política de Privacidade'}</Link></li>
              <li><Link to={isSpanish ? '/es/terminos-de-uso' : '/termos-de-uso'} className={linkClass}>{isSpanish ? 'Términos de uso' : 'Termos de Uso'}</Link></li>
              <li className="flex items-center gap-2 pt-1 text-[var(--on-forest-2)]">
                <SafeIcon name="Mail" className="h-4 w-4 shrink-0 text-[var(--on-forest-2)]" />
                <ContactEmail />
              </li>
            </ul>
          </nav>
        </div>

        <div className="border-t border-[var(--forest-line)] pt-6 text-xs leading-relaxed text-[var(--on-forest-2)]">
          <p>
              &copy; {new Date().getFullYear()} MiniMental{isSpanish ? '.org' : '.com.br'} — Dr. Carlos Felipe CRM-MG 105.721. {isSpanish ? 'Todos los derechos reservados.' : 'Todos os direitos reservados.'}
          </p>
          <p className="mt-2 max-w-2xl">
            {isSpanish ? 'La calculadora pública funciona localmente en el navegador. Los registros guardados en MiniMental PRO permanecen privados y vinculados a la cuenta profesional.' : 'A calculadora pública funciona localmente no navegador. Registros salvos no MiniMental PRO ficam separados, privados e vinculados à conta do profissional.'}
          </p>
        </div>
      </div>
    </footer>
  );
}
